import { useEffect, useRef } from "react";
import { toast } from "@/lib/toast";
import { isUpdateSafeNow } from "@/lib/updateSafety";

export const AppUpdateManager = (): null => {
    const updateFnRef = useRef<(() => Promise<void>) | null>(null);
    const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
    const lastActivity = useRef<number>(Date.now());
    const INACTIVITY_LIMIT = 60 * 1000; // 1 minute

    const handleUpdate = async () => {
        if (updateFnRef.current) {
            await updateFnRef.current();
        }
    };

    useEffect(() => {
        // Disable update management on native platforms (Android/iOS)
        // Native apps are updated through the App Store/Play Store
        if ((window as any).Capacitor?.isNativePlatform()) {
            return;
        }

        let hasUpdate = false;

        const startInactivityTimer = () => {
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
            inactivityTimer.current = setTimeout(() => {
                if (Date.now() - lastActivity.current >= INACTIVITY_LIMIT) {
                    // Auto-update reloads the page — never while a login/OAuth
                    // return is in flight (it would eat the callback tokens).
                    if (!isUpdateSafeNow()) {
                        console.log("[AppUpdateManager] Update deferred: login in progress.");
                        startInactivityTimer(); // re-arm; try again after the next quiet minute
                        return;
                    }
                    console.log("[AppUpdateManager] User inactive for > 1min, auto-updating.");
                    handleUpdate();
                }
            }, INACTIVITY_LIMIT);
        };

        const resetInactivityTimer = () => {
            lastActivity.current = Date.now();
            if (inactivityTimer.current) {
                clearTimeout(inactivityTimer.current);
                inactivityTimer.current = null;
            }
            if (hasUpdate) startInactivityTimer();
        };

        const onUpdateAvailable = (event: any) => {
            console.log("[AppUpdateManager] Update notification received");
            hasUpdate = true;
            updateFnRef.current = event.detail.updateServiceWorker;

            toast.info("Nieuwe versie beschikbaar", {
                channel: 'general',
                description: "Tik op 'Vernieuwen' om de app bij te werken.",
                duration: Infinity,
                action: {
                    label: "Vernieuwen",
                    onClick: () => {
                        event.detail.updateServiceWorker();
                    }
                }
            });

            startInactivityTimer();
        };

        // Show a subtle confirmation when the app was just updated
        const onUpdated = () => {
            toast.success("App is bijgewerkt naar de nieuwste versie!", { channel: 'general', duration: 3000 });
        };

        window.addEventListener('app-update-available', onUpdateAvailable);
        window.addEventListener('app-updated', onUpdated);

        const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        activityEvents.forEach(event => {
            window.addEventListener(event, resetInactivityTimer);
        });

        return () => {
            window.removeEventListener('app-update-available', onUpdateAvailable);
            window.removeEventListener('app-updated', onUpdated);
            activityEvents.forEach(event => {
                window.removeEventListener(event, resetInactivityTimer);
            });
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        };
    }, []);

    return null;
};
